import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

const inputWrapperStyles = cva(
  'group flex w-full items-stretch overflow-hidden rounded-md border bg-bg-canvas text-fg-primary transition-base ease-token focus-within:ring-2 focus-within:ring-accent disabled:opacity-50',
  {
    variants: {
      size: {
        sm: 'h-8 text-sm',
        md: 'h-10 text-base',
        lg: 'h-12 text-lg',
      },
      tone: {
        default: 'border-border-subtle hover:border-border-strong',
        error: 'border-danger focus-within:ring-danger',
      },
    },
    defaultVariants: { size: 'md', tone: 'default' },
  },
);

const fieldPad = {
  sm: 'px-2',
  md: 'px-3',
  lg: 'px-4',
} as const;

export interface InputProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'>,
    VariantProps<typeof inputWrapperStyles> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Static content rendered inside the input shell, before the field (icon, currency, etc.). */
  prefix?: ReactNode;
  /** Static content rendered inside the input shell, after the field. */
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size, tone, label, hint, error, prefix, suffix, id, disabled, ...props },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const describedById = hint || error ? `${inputId}-desc` : undefined;
  const resolvedTone = error ? 'error' : tone;
  const pad = fieldPad[(size ?? 'md') as keyof typeof fieldPad];

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="text-fg-primary text-sm font-medium">
          {label}
        </label>
      )}
      <div className={cn(inputWrapperStyles({ size, tone: resolvedTone }), className)}>
        {prefix && (
          <span
            className={cn('text-fg-muted flex shrink-0 items-center pl-3 pr-1', !label && 'pl-3')}
          >
            {prefix}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          className={cn(
            'placeholder:text-fg-muted block h-full w-full bg-transparent outline-none disabled:cursor-not-allowed',
            !prefix && pad,
            prefix && 'pr-3',
            suffix && (prefix ? 'px-1' : `${pad} pr-1`),
          )}
          {...props}
        />
        {suffix && (
          <span className="text-fg-muted flex shrink-0 items-center pl-1 pr-3">{suffix}</span>
        )}
      </div>
      {(hint || error) && (
        <p id={describedById} className={cn('text-xs', error ? 'text-danger' : 'text-fg-muted')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
